import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { resolveTabs } from '../../i18n/resolveTabs';
import TimeCompanyPage from './TimeCompanyPage';
import TimeEffectivePage from './TimeEffectivePage';
import ProductionTimeGrid from '../tdabc/ProductionTimeGrid';
import CapacityOverviewTab from '../tdabc/CapacityOverviewTab';
import RentabiliteClientPage from '../tdabc/RentabiliteClientPage';

/* Temps — section créée le 2026-08-21 au soir : le TEMPS a son sujet.
     • Calendrier      = TimeCompanyPage   (heures/semaine, fériés, vacances
                         std → d'où découlent TT et TO)
     • Temps standard  = TimeEffectivePage (TT → TO → TR, la cascade entière —
                         venu du hub CCR, qui le LIT sans le porter)
   Le calendrier POSE le temps, le temps standard le DÉRIVE puis le RÉPARTIT —
   LCR et CCR consomment.

   Un troisième onglet « Temps ouvert » (TimeStartingPointPage) s'arrêtait à TO
   avec les mêmes colonnes ; retiré le 2026-08-23. Deux écrans pour un même TO
   ne valaient rien, d'autant que leurs totaux divergeaient : l'un totalisait
   les lignes affichées, l'autre les totaux annuels du moteur.

   Les deux LECTURES de ce temps (vue d'ensemble, audit) y ont vécu du
   2026-08-22 au 2026-08-26 ; elles forment désormais leur propre section,
   « Pilotage temps » (`TimePilotHub`). Ici on SAISIT le temps, là on le LIT :
   un séparateur d'onglets disait déjà que la nature changeait, une section le
   dit mieux.

   SÉPARATION DU 2026-08-27 : le hub sert DEUX sections. Le calendrier reste
   dans People (« Calendrier », Saisie et réglages) — c'est un réglage de
   l'entreprise ; le temps standard part dans Rentabilité, en tête de la chaîne
   CCR → coût de production → marge par service. `only` restreint le hub à
   l'onglet de sa section ; le composant, lui, reste unique.
   Gabarit unique : <PageShell framed> + <Tabs> + sous-pages en embedded. */
/* « Temps de production » rejoint la saisie le 2026-08-30 : il vivait dans
   « Coût de production », entre deux LECTURES, alors qu'on y saisit des heures.
   Toute la saisie du temps tient désormais dans une seule section. */
const TAB_DEFS = [
  { id: 'calendar',   labelKey: 'people:lcr.tabs.calendar' },
  /* La cascade OUVRE la section le 2026-08-30 : on voit où l'on en est avant
     de saisir les heures qui la nourrissent. Venue de « Pilotage temps ». */
  { id: 'overview',   labelKey: 'rentabilite:capacity.tabs.overview' },
  { id: 'standard',   labelKey: 'people:ccr.tabs.standard' },
  /* Deux vues des heures TF (2026-08-30) : l'ANNÉE, où les usages du TR se
     saisissent, puis le détail MENSUEL du facturable. Une seule table de
     dix-huit colonnes portait les deux. */
  { id: 'production',        labelKey: 'rentabilite:production.tabs.temps-prod' },
  /* Le temps par ACTIVITÉ suit les heures de l'année : c'est la même saisie,
     vue par l'autre bout — on a compté les heures d'une personne, on dit
     maintenant à quelles activités elles sont allées (2026-09-01). Sa section
     propre n'a vécu qu'un jour ; la page reste routée. */
  { id: 'allocation',        labelKey: 'nav:items.rc-allocation' },
  /* Le DÉTAIL mensuel ferme la barre : on saisit l'année, on la ventile par
     activité, et l'on descend au mois seulement pour le facturable. */
  { id: 'production-months', labelKey: 'rentabilite:production.tabs.temps-prod-m' },
];

export default function TimeHub({ activeTenant, addToast, year, onYearChange, onTenantUpdated, initialTab, only = null, title = null }) {
  const { t } = useTranslation();
  const p = useP();
  /* `only` accepte un onglet ou une LISTE : la section « Calendrier » n'en sert
     qu'un, « Saisie des temps » en sert deux. */
  const keep = only == null ? null : (Array.isArray(only) ? only : [only]);
  const defs = keep ? TAB_DEFS.filter(d => keep.includes(d.id)) : TAB_DEFS;
  /* `initialTab` : onglet visé par un lien venu d'une autre section (le récit
     CCR pointe « Temps standard »). Sans lui, on retombait sur le Calendrier. */
  const [tab, setTab] = useState((keep && keep[0]) || initialTab || 'calendar');
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  useEffect(() => { if (keep && !keep.includes(tab)) setTab(keep[0]); }, [keep, tab]);
  const TABS = resolveTabs(defs, t);

  const renderTab = (id) => {
    switch (id) {
      case 'calendar':
        return <TimeCompanyPage embedded activeTenant={activeTenant} year={year} onYearChange={onYearChange} onTenantUpdated={onTenantUpdated} />;
      case 'overview':
        return <CapacityOverviewTab activeTenant={activeTenant} addToast={addToast} year={year} />;
      case 'standard':
        return <TimeEffectivePage embedded activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'allocation':
        /* Titre explicite : sans lui, la page retombe sur « Rentabilité
           service », le nom de son écran d'origine (2026-09-01). */
        return <RentabiliteClientPage only="saisie" title={p('Heures facturables par activités / services')}
                                      activeTenant={activeTenant} addToast={addToast} year={year} />;
      case 'production':
        return <ProductionTimeGrid part="annual" activeTenant={activeTenant} addToast={addToast} year={year} />;
      case 'production-months':
        return <ProductionTimeGrid part="monthly" activeTenant={activeTenant} addToast={addToast} year={year} />;
      default:
        return null;
    }
  };

  const excel = useHubExcelExport({
    tabs: TABS.map(tb => ({ label: tb.label, render: () => renderTab(tb.id) })),
    fileBase: title || t('people:time.title'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run]));

  return (
    <PageShell framed title={title || t('people:time.title')}>
      {/* Une section restreinte à UN onglet n'a pas de barre d'onglets : il n'y
          a rien à choisir. */}
      {TABS.length > 1 && <Tabs tabs={TABS} active={tab} onChange={setTab} />}
      <div className="page-shell__panel">{renderTab(tab)}</div>
      {excel.stage}
    </PageShell>
  );
}
